/**
 * Certificate scans (fix/cert-scan, owner 2026-10-08): "scanning the certificate and automatically putting it with the right
 * person", "suggest who it belongs to", "fast adding", "store it as proof right away and move quickly".
 *
 *  1. Save first. The ORIGINAL file (photo, screenshot, PDF, HEIC) is stored the moment it arrives, as proof, before any read.
 *  2. Read second. A model reads it into CertReading rows (one PDF may hold several certificates). The read pre-fills; it never
 *     verifies. A failed or slow read leaves the proof saved, with "Add the dates" as the next step.
 *  3. Suggest the person. suggestHolder ranks the place's people by name (exact, nickname, typo), then breaks ties with whoever
 *     is missing that kind of certificate and whoever is due soonest. It always proposes someone (or "Someone new" when no name
 *     is close); it never files silently: a person confirms with one tap ("File all").
 *  4. A course certificate is not a permit (onboarding.ts): it marks the course done and carries the permit window.
 *
 * Clearance still needs a manager's "I checked the card" (people.ts verify), as before.
 */
import type { Credential, CredentialKind, Id, IsoDate, IsoDateTime } from "./model.ts";
import { editDistance } from "./things.ts";
import { LOCAL_DEPARTMENTS } from "./states/ut-local.ts";

/** What the model may say a certificate is. "unknown" = it couldn't tell, or it's a kind the app doesn't track. */
export type CertReadKind = Extract<CredentialKind, "food_handler_course" | "food_handler_permit" | "certified_food_safety_manager" | "allergen_awareness"> | "unknown";
export const CERT_READ_KINDS: readonly CertReadKind[] = ["food_handler_course", "food_handler_permit", "certified_food_safety_manager", "allergen_awareness", "unknown"];

export type CertField = "kind" | "holderName" | "issuer" | "number" | "issuedOn" | "expiresOn" | "state" | "county";
export const CERT_FIELDS: readonly CertField[] = ["kind", "holderName", "issuer", "number", "issuedOn", "expiresOn", "state", "county"];

/** One certificate as read from a file. Every value is what is printed, or null; confidence 0..1 per field. */
export interface CertReading {
  kind: CertReadKind;
  holderName: string | null;
  issuer: string | null;
  number: string | null;
  issuedOn: IsoDate | null;
  expiresOn: IsoDate | null;
  /** Two-letter state the certificate is for ("UT"), when printed. */
  state: string | null;
  county: string | null;
  /** The issuer's verify note as printed ("Scan to Verify"), and a URL only when one is printed in text. Never fetched. */
  verifyNote: string | null;
  verifyUrl: string | null;
  confidence: Record<CertField, number>;
}

/** One person the certificate could belong to. */
export interface HolderCandidate { employeeId: Id; name: string; score: number }

/**
 * The suggestion shown on a review row. `pick` is pre-selected: a person, or "new" (add the person from the certificate's
 * name) when no one on the team is close. `alternates` are the next close people, one tap each. `clear` = one person is a clear
 * match by name (a single scan may then be filed straight away, with Undo and "Not <name>?").
 */
export interface HolderSuggestion {
  pick: { kind: "person"; employeeId: Id; name: string } | { kind: "new"; name: string };
  alternates: HolderCandidate[];
  clear: boolean;
  /** A worker scanning their own: the name printed doesn't look like theirs ("The name on it is …"). */
  nameMismatch?: boolean;
}

/** One certificate row in a scan: the reading, the suggestion, and where it was filed once a person confirmed. */
export interface CertRow {
  reading: CertReading;
  suggestion: HolderSuggestion;
  filed?: { credentialId: Id; employeeId: Id; at: IsoDateTime; by: Id };
  /** Filed from a person's outstanding slot ("Add proof"): no suggestion step; the person's choice of kind stands. */
  slot?: boolean;
  /** A person corrected the read (kind) or the suggestion (person). Counted for read accuracy; no personal details. */
  corrected?: { kind?: boolean; person?: boolean };
}

/**
 * A saved certificate file (proof) and what was read from it. Stored the moment it arrives; the read fills `rows` later.
 * `filePath` holds the original bytes as uploaded; `viewPath` a converted copy (HEIC → JPEG) for display and the model.
 */
export interface CredentialScan {
  id: Id;
  orgId: Id;
  locationId?: Id;
  /** The file's name as picked, for the review row ("IMG_2041.HEIC"). */
  fileName?: string;
  filePath: string;
  mimeType: string;
  byteSize: number;
  sha256: string;
  viewPath?: string;
  /** reading: saved, the read is running. read: rows filled. unread: the read failed or timed out; the proof is still saved. */
  status: "reading" | "read" | "unread";
  /** Plain words when unread ("Couldn't read the dates. Add them."). */
  error?: string;
  /** Who it's for before any read: the person whose page it was dropped on, or the worker's own record. */
  forEmployeeId?: Id;
  rows: CertRow[];
  createdBy: Id;
  createdAt: IsoDateTime;
  updatedAt: IsoDateTime;
}

/** The line a review row shows for a saved file before and after the read. */
export function scanStatusLine(s: Pick<CredentialScan, "status" | "rows">): string {
  if (s.status === "reading") return "Saved as proof. Reading it…";
  if (s.status === "unread" || !s.rows.length) return "Saved as proof. Add the dates.";
  return "Saved as proof.";
}

// ── Reading validation ───────────────────────────────────────────────────────────────────────────────────────────────

export class CertReadingError extends Error {}

const ISO = /^\d{4}-\d{2}-\d{2}$/;
const realDate = (s: string) => ISO.test(s) && !Number.isNaN(Date.parse(`${s}T00:00:00Z`)) && new Date(`${s}T00:00:00Z`).toISOString().slice(0, 10) === s;
const clip = (v: unknown, n: number): string | null => (typeof v === "string" && v.trim() ? v.trim().replace(/\s+/g, " ").slice(0, n) : null);

/**
 * The model's structured output → CertReading[]. Strict on shape, forgiving on values: a bad date or kind becomes null /
 * "unknown" with confidence 0 (the person types it in), never a guess. Throws only when the shape itself is wrong.
 */
export function validateCertReadings(x: unknown): CertReading[] {
  if (!x || typeof x !== "object" || !Array.isArray((x as { certificates?: unknown }).certificates)) throw new CertReadingError("Expected { certificates: [...] }.");
  const list = (x as { certificates: unknown[] }).certificates.slice(0, 20);
  return list.map((c, i) => {
    if (!c || typeof c !== "object") throw new CertReadingError(`certificates[${i}] isn't an object.`);
    const o = c as Record<string, unknown>;
    const conf = (o.confidence && typeof o.confidence === "object" ? o.confidence : {}) as Record<string, unknown>;
    const confidence = Object.fromEntries(CERT_FIELDS.map((f) => {
      const n = typeof conf[f] === "number" && Number.isFinite(conf[f]) ? Math.max(0, Math.min(1, conf[f] as number)) : 0;
      return [f, n];
    })) as Record<CertField, number>;
    const date = (f: "issuedOn" | "expiresOn") => { const v = clip(o[f], 10); if (v && realDate(v)) return v; confidence[f] = 0; return null; };
    const kind = CERT_READ_KINDS.includes(o.kind as CertReadKind) ? (o.kind as CertReadKind) : "unknown";
    if (kind === "unknown") confidence.kind = Math.min(confidence.kind, 0.5);
    const state = clip(o.state, 20)?.toUpperCase() ?? null;
    const url = clip(o.verifyUrl, 300);
    const r: CertReading = {
      kind,
      holderName: clip(o.holderName, 80),
      issuer: clip(o.issuer, 120),
      number: clip(o.number, 60),
      issuedOn: date("issuedOn"),
      expiresOn: date("expiresOn"),
      state: state && /^[A-Z]{2}$/.test(state) ? state : null,
      county: clip(o.county, 60),
      verifyNote: clip(o.verifyNote, 200),
      // Only an https link printed as text is shown, and only as a link the person may open. Never fetched by the app.
      verifyUrl: url && /^https:\/\/[^\s]+$/.test(url) ? url : null,
      confidence,
    };
    for (const f of ["holderName", "issuer", "number", "county"] as const) if (r[f] === null) r.confidence[f] = 0;
    if (r.state === null) r.confidence.state = 0;
    return r;
  });
}

/** An empty reading for a file that couldn't be read: the person types it in. */
export function blankReading(): CertReading {
  return { kind: "unknown", holderName: null, issuer: null, number: null, issuedOn: null, expiresOn: null, state: null, county: null, verifyNote: null, verifyUrl: null, confidence: Object.fromEntries(CERT_FIELDS.map((f) => [f, 0])) as Record<CertField, number> };
}

/** Fields the person should look at: read with low confidence, or not read at all. */
export function fieldsToCheck(r: CertReading, min = 0.7): CertField[] {
  return CERT_FIELDS.filter((f) => f !== "state" && f !== "county" && r.confidence[f] < min);
}

// ── Name matching ────────────────────────────────────────────────────────────────────────────────────────────────────

/** Common English given-name groups (formal and short forms). Prefixes ("Jon" in "Jonathan") and typos are handled below. */
const NICK_GROUPS: readonly string[][] = [
  ["zachary", "zackary", "zakary", "zakery", "zachery", "zach", "zack", "zak"],
  ["alexander", "alexandra", "alex", "sasha", "xander", "lexi"],
  ["robert", "rob", "bob", "bobby", "robbie", "bert"],
  ["william", "will", "bill", "billy", "liam", "willy"],
  ["richard", "rick", "ricky", "rich", "dick"],
  ["james", "jim", "jimmy", "jamie"],
  ["john", "jack", "johnny", "jon"],
  ["jonathan", "jon", "jonny", "nathan"],
  ["michael", "mike", "mikey", "mick"],
  ["christopher", "chris", "topher", "kit"],
  ["christina", "christine", "chris", "tina", "chrissy"],
  ["elizabeth", "liz", "lizzie", "beth", "betty", "eliza", "libby"],
  ["katherine", "catherine", "kathryn", "kate", "katie", "kathy", "cathy", "kat"],
  ["margaret", "maggie", "meg", "peggy", "marge"],
  ["jennifer", "jen", "jenny"],
  ["jessica", "jess", "jessie"],
  ["joseph", "joe", "joey"],
  ["daniel", "dan", "danny"],
  ["david", "dave", "davey"],
  ["matthew", "matt", "matty"],
  ["anthony", "tony"],
  ["benjamin", "ben", "benny"],
  ["samuel", "sam", "sammy"],
  ["samantha", "sam", "sammy"],
  ["thomas", "tom", "tommy"],
  ["andrew", "andy", "drew"],
  ["nicholas", "nick", "nicky"],
  ["edward", "ed", "eddie", "ted", "ned"],
  ["steven", "stephen", "steve"],
  ["patricia", "pat", "patty", "trish"],
  ["patrick", "pat", "paddy"],
  ["rebecca", "becky", "becca"],
  ["victoria", "vicky", "tori"],
  ["isabella", "isabel", "bella", "izzy"],
  ["gabriel", "gabe"],
  ["gabriela", "gabriella", "gabby"],
  ["francisco", "paco", "frank", "pancho"],
  ["francis", "frank", "frankie"],
  ["guadalupe", "lupe", "lupita"],
  ["jose", "pepe"],
  ["alejandro", "alex", "ale"],
  ["josephine", "jo", "josie"],
  ["timothy", "tim", "timmy"],
  ["kenneth", "ken", "kenny"],
  ["ronald", "ron", "ronnie"],
  ["donald", "don", "donnie"],
  ["gregory", "greg"],
  ["jeffrey", "geoffrey", "jeff", "geoff"],
  ["susan", "sue", "susie"],
  ["deborah", "debra", "deb", "debbie"],
  ["abigail", "abby", "abbie"],
  ["madison", "maddie", "maddy"],
  ["olivia", "liv", "livvy"],
  ["emily", "em", "emmy"],
  ["emma", "em"],
];
const NICK = new Map<string, Set<number>>();
NICK_GROUPS.forEach((g, i) => g.forEach((n) => { const s = NICK.get(n) ?? new Set<number>(); s.add(i); NICK.set(n, s); }));
const sameGroup = (a: string, b: string) => { const x = NICK.get(a), y = NICK.get(b); return !!x && !!y && [...x].some((i) => y.has(i)); };

/** Lowercase, no accents or punctuation, "Pierce, Jonathan" → ["jonathan", "pierce"]. */
export function nameTokens(name: string): string[] {
  let n = name.normalize("NFKD").replace(/[̀-ͯ]/g, "").toLowerCase().trim();
  if (/^[^,]+,[^,]+$/.test(n)) { const [last, first] = n.split(","); n = `${first} ${last}`; }
  return n.replace(/[^a-z\s'-]/g, " ").replace(/['-]/g, "").split(/\s+/).filter((t) => t && !["jr", "sr", "ii", "iii", "iv", "mr", "mrs", "ms", "dr"].includes(t));
}

const typo = (a: string, b: string): number => {
  const d = editDistance(a, b), n = Math.min(a.length, b.length);
  if (d === 1 && n >= 4) return 0.85;
  if (d === 2 && n >= 6) return 0.7;
  return 0;
};

/** Given names: equal 1, nickname or short form 0.9, typo 0.85/0.7, an initial 0.6. */
function firstScore(a: string, b: string): number {
  if (a === b) return 1;
  if (sameGroup(a, b)) return 0.9;
  const [s, l] = a.length <= b.length ? [a, b] : [b, a];
  if (s.length >= 3 && l.startsWith(s)) return 0.9; // "Jon" / "Jonathan", "Alex" / "Alexis"
  if (s.length === 1 && l.startsWith(s)) return 0.6;
  return typo(a, b);
}

/** Family names: equal 1, typo 0.85/0.7. */
function lastScore(a: string, b: string): number {
  if (a === b) return 1;
  return typo(a, b);
}

/**
 * How well a printed name matches a person's name, 0..1. 1 = the same words. "Jonathan Pierce" vs "Jon Pierce" ≈ 0.95.
 * A person with only a first name ("Sam") can at most reach 0.6, so they never become a clear match alone.
 */
export function nameScore(printed: string, person: string): number {
  const a = nameTokens(printed), b = nameTokens(person);
  if (!a.length || !b.length) return 0;
  if (a.join(" ") === b.join(" ")) return 1;
  const f = firstScore(a[0]!, b[0]!);
  if (a.length === 1 || b.length === 1) {
    // One side has one word: it may be either name.
    const one = a.length === 1 ? a[0]! : b[0]!, other = a.length === 1 ? b : a;
    return Math.max(firstScore(one, other[0]!), lastScore(one, other[other.length - 1]!)) * 0.6;
  }
  const l = lastScore(a[a.length - 1]!, b[b.length - 1]!);
  if (!l) return f * 0.35;
  if (!f) return l * 0.5;
  return Math.round((0.5 * f + 0.5 * l) * 100) / 100;
}

export interface HolderPerson {
  id: Id;
  name: string;
  /** True when this person has no current certificate of the read kind (a tie goes to them). */
  missingKind?: boolean;
  /** Days until their card or course is due (smaller = sooner; a tie goes to them). null = nothing due. */
  dueInDays?: number | null;
}

export const CLEAR_SCORE = 0.85;
const CLOSE_SCORE = 0.5;

/**
 * Who the certificate belongs to. Name first; ties (scores within 0.05) go to whoever is missing that kind of certificate,
 * then whoever is due soonest, then the page it was dropped on (`hint`). `self` set (a worker scanning their own): the only
 * choice is them; a name that doesn't look like theirs is flagged, never refiled to someone else.
 */
export function suggestHolder(holderName: string | null, people: readonly HolderPerson[], o: { hint?: Id; self?: HolderPerson } = {}): HolderSuggestion {
  if (o.self) {
    const s = holderName ? nameScore(holderName, o.self.name) : 0;
    return { pick: { kind: "person", employeeId: o.self.id, name: o.self.name }, alternates: [], clear: s >= CLEAR_SCORE, ...(holderName && s < CLEAR_SCORE ? { nameMismatch: true } : {}) };
  }
  const scored = people.map((p) => ({ p, score: holderName ? nameScore(holderName, p.name) : 0 }));
  const bucket = (n: number) => Math.round(n * 20) / 20;
  scored.sort((x, y) =>
    bucket(y.score) - bucket(x.score)
    || Number(!!y.p.missingKind) - Number(!!x.p.missingKind)
    || (x.p.dueInDays ?? Infinity) - (y.p.dueInDays ?? Infinity)
    || Number(y.p.id === o.hint) - Number(x.p.id === o.hint)
    || y.score - x.score
    || x.p.name.localeCompare(y.p.name));
  const cand = (x: (typeof scored)[number]): HolderCandidate => ({ employeeId: x.p.id, name: x.p.name, score: x.score });
  const top = scored[0];
  const hinted = o.hint ? scored.find((x) => x.p.id === o.hint) : undefined;
  // Dropped on a person's page and the name doesn't say otherwise: that person.
  if (hinted && (!holderName || hinted.score >= CLOSE_SCORE) && (!top || bucket(top.score) <= bucket(hinted.score))) {
    return { pick: { kind: "person", employeeId: hinted.p.id, name: hinted.p.name }, alternates: scored.filter((x) => x !== hinted).slice(0, 2).map(cand), clear: !!holderName && hinted.score >= CLEAR_SCORE && !scored.some((x) => x !== hinted && x.score >= hinted.score - 0.1) };
  }
  if (!top || top.score < CLOSE_SCORE) {
    // Nobody is close: "Someone new" is pre-selected (when there's a name), the nearest people one tap away.
    if (holderName) return { pick: { kind: "new", name: holderName }, alternates: scored.slice(0, 2).map(cand), clear: false };
    if (!top) return { pick: { kind: "new", name: "" }, alternates: [], clear: false };
    return { pick: { kind: "person", employeeId: top.p.id, name: top.p.name }, alternates: scored.slice(1, 3).map(cand), clear: false };
  }
  const second = scored[1];
  const clear = top.score >= CLEAR_SCORE && (!second || second.score < top.score - 0.1);
  return { pick: { kind: "person", employeeId: top.p.id, name: top.p.name }, alternates: scored.slice(1, 3).map(cand), clear };
}

// ── Kinds, the course window, unknowns ───────────────────────────────────────────────────────────────────────────────

export const CERT_KIND_LABEL: Record<CertReadKind, string> = {
  food_handler_course: "Food handler course certificate",
  food_handler_permit: "Food handler permit",
  certified_food_safety_manager: "Food safety manager certificate",
  allergen_awareness: "Allergen awareness certificate",
  unknown: "Unknown kind",
};

/** The credential kinds a scan row can be filed as (the person picks one when the read said "unknown"). */
export const FILEABLE_KINDS: readonly CredentialKind[] = ["food_handler_course", "food_handler_permit", "certified_food_safety_manager", "allergen_awareness", "choking_first_aid"];

/**
 * A plain note when the read can't be trusted for this place: an unknown kind, or a certificate for another state. Never a
 * verdict on validity: "unknown, confirm with <authority>".
 */
export function certCaution(r: Pick<CertReading, "kind" | "state">, place: { state?: string | null }, authority: string): string | null {
  if (r.kind === "unknown") return `Unknown kind of certificate. Confirm with ${authority} whether it counts here.`;
  if (r.state && place.state && r.state !== place.state.toUpperCase()) return `This certificate is for ${r.state}. Whether it counts here is unknown: confirm with ${authority}.`;
  return null;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
/** "Oct 27" (or "Oct 27, 2027" outside `today`'s year). */
export function certDay(d: IsoDate, today?: IsoDate): string {
  const m = `${MONTHS[Number(d.slice(5, 7)) - 1]} ${Number(d.slice(8, 10))}`;
  return today && today.slice(0, 4) === d.slice(0, 4) ? m : `${m}, ${d.slice(0, 4)}`;
}

/** A course certificate on file (onboarding.ts OnboardingStatus.course). */
export interface CourseOnFile {
  expiresOn: string | null;
  /** The day to have the permit by: the earlier of the certificate's own end and the place's permit window; null = unknown. */
  permitBy: IsoDate | null;
  /** Days until permitBy (negative = past it). */
  daysLeft: number | null;
  verified: boolean;
  /** The rule the permit window comes from ("Utah Code 26B-7-413"); "" when the place has no card rule. */
  citation: string;
}

/** "Course done — get your permit by Oct 27" (the brief's words), or the window when no date is known. */
export function courseLine(c: CourseOnFile, today?: IsoDate, authority = "your local health department"): string {
  if (c.permitBy && c.daysLeft !== null && c.daysLeft < 0) return `Course certificate ended ${certDay(c.permitBy, today)}. The permit isn't on file yet.`;
  if (c.permitBy) return `Course done — get your permit by ${certDay(c.permitBy, today)}${c.citation ? ` (${c.citation})` : ""}.`;
  return `Course done — get your permit. The deadline is unknown: confirm with ${authority}.`;
}

/** Credential fields a filed row writes, from a reading plus the person's corrections. */
export function credentialFromReading(r: CertReading, kind: CredentialKind): Pick<Credential, "kind" | "holderName" | "number" | "issuedOn" | "expiresOn" | "issuer" | "issuingCounty"> {
  return {
    kind,
    ...(r.holderName ? { holderName: r.holderName } : {}),
    ...(r.number ? { number: r.number } : {}),
    ...(r.issuedOn ? { issuedOn: r.issuedOn } : {}),
    ...(r.expiresOn ? { expiresOn: r.expiresOn } : {}),
    ...(r.issuer ? { issuer: r.issuer } : {}),
    ...(r.county ? { issuingCounty: r.county } : {}),
  };
}

// ── Outstanding slots: what each person still needs, by type ─────────────────────────────────────────────────────────

/** One certificate a person is still missing or due on. The manual picker leads with these, most urgent first. */
export interface CertSlot {
  kind: CredentialKind;
  /** "Food handler permit (due Oct 27)", "Food safety manager certificate (missing)". */
  label: string;
  /** Days until it's due (negative = late); null = missing with no date. */
  dueInDays: number | null;
  /** One on file that runs out soon ("renew by"), not one that is missing. Shown under "Renew soon", never "Still needed". */
  renew?: boolean;
}

const SLOT_NAME: Record<CredentialKind, string> = {
  food_handler_course: "Food handler course",
  food_handler_permit: "Food handler permit",
  certified_food_safety_manager: "Food safety manager certificate",
  allergen_awareness: "Allergen awareness certificate",
  choking_first_aid: "Choking first aid",
};
const plusDays = (d: IsoDate, n: number) => new Date(Date.parse(d + "T00:00:00Z") + n * 86400000).toISOString().slice(0, 10);

/**
 * The slots one person is missing or due on, from their onboarding status (course and permit steps, the card's expiry, the
 * course certificate's permit-by day), the place's extra certificate kinds (credentialKindsAt beyond the base two), and the
 * manager certificate where the person in charge needs one. Sorted: soonest due first, then missing with no date.
 */
export function outstandingSlots(st: {
  steps: { step: string; done: boolean }[];
  courseDueInDays: number;
  permitDueInDays: number;
  clockRunning?: boolean;
  card: { kind: string; expiresOn: string | null; daysLeft: number | null } | null;
  course: CourseOnFile | null;
}, o: { today: IsoDate; held: readonly Pick<Credential, "kind" | "expiresOn">[]; extraKinds?: readonly CredentialKind[]; needsManagerCert?: boolean }): CertSlot[] {
  const out: CertSlot[] = [];
  const step = (k: string) => st.steps.find((x) => x.step === k);
  const running = st.clockRunning !== false;
  const due = (kind: CredentialKind, n: number | null, verb = "due") => out.push({ kind, dueInDays: n, label: `${SLOT_NAME[kind]} (${n === null ? "missing" : n < 0 ? `${verb} ${certDay(plusDays(o.today, n), o.today)}, late` : `${verb} ${certDay(plusDays(o.today, n), o.today)}`})`, ...(verb === "renew by" ? { renew: true } : {}) });
  const course = step("food_handler_course");
  if (course && !course.done) due("food_handler_course", running ? st.courseDueInDays : null);
  const permitStep = step("credential_uploaded");
  if (permitStep && !permitStep.done && !st.card) due("food_handler_permit", st.course?.daysLeft ?? (running ? st.permitDueInDays : null));
  else if (st.card && st.card.kind === "food_handler_permit" && st.card.daysLeft !== null && st.card.daysLeft <= 30) due("food_handler_permit", st.card.daysLeft, "renew by");
  const current = (k: CredentialKind) => o.held.some((c) => c.kind === k && (!c.expiresOn || c.expiresOn >= o.today));
  if (o.needsManagerCert && !current("certified_food_safety_manager")) due("certified_food_safety_manager", null);
  for (const k of o.extraKinds ?? []) if (!current(k) && !out.some((x) => x.kind === k)) due(k, null);
  return out.sort((a, b) => (a.dueInDays ?? Infinity) - (b.dueInDays ?? Infinity));
}

// ── API shapes shared by the web and phone apps (services/api certscan/pipeline.ts scanView) ─────────────────────────

/** A saved certificate file and its rows, as GET/POST /certificate-scans return it (services/api certscan/pipeline.ts scanView). */
export interface CertScanRowView {
  index: number;
  reading: CertReading;
  suggestion: HolderSuggestion;
  kindLabel: string;
  caution: string | null;
  kindNote: string | null;
  toCheck: string[];
  openChoices: boolean;
  filed: { credentialId: string; employeeId: string; employeeName: string | null; kind: CredentialKind | null; verified: boolean; at: string; by: string } | null;
  slot?: boolean;
  /** A course certificate filed here, where the place's rule gives it a window: the last day it counts, and the rule. */
  goodUntil?: string | null;
  goodUntilCitation?: string | null;
}
export interface CertScanView {
  id: string; status: "reading" | "read" | "unread"; statusLine: string; error: string | null; fileName: string | null; mimeType: string; byteSize: number;
  /** null for a worker when the file also holds someone else's certificate. */
  fileUrl: string | null; viewUrl: string | null; forEmployeeId: string | null; createdAt: string; authority: string; rows: CertScanRowView[];
}
export interface CertSaveBody { fileBase64: string; fileName?: string; forEmployeeId?: string; slotKind?: CredentialKind; locationId?: string }
/** One row to file ("File all", "Wrong type?", "Wrong person?"): a person on the team or someone new, the kind, and any typed field. */
export interface CertFileItem { scanId: string; row: number; kind: CredentialKind; employeeId?: string; newPerson?: string; holderName?: string; number?: string; issuedOn?: string; expiresOn?: string; issuer?: string }
export interface CertSlotsRow { employeeId: string; name: string; slots: CertSlot[] }

// ── The onboarding permit step's upload (fix/card-step-pdf; fix/permit-step-read, owner 2026-10-09) ────────────────────────

/** The server's default upload cap (services/api config maxUploadBytes, MAX_UPLOAD_MB=8). The phone and web check it first so a
 *  too-big file is turned away in plain words before a long upload; the server's own check is the one that counts. */
export const CARD_UPLOAD_MAX_BYTES = 8 * 1024 * 1024;
export const CARD_UPLOAD_TOO_BIG = "That file is too big. The limit is 8 MB. Take a photo of your permit instead.";

/**
 * The onboarding step's words (owner 2026-10-09: "what card anyways": "card" also means the payment card, so the food handler
 * credential is the "permit" everywhere a worker or manager reads it; "card" stays for payment only). One set for web and phone.
 */
export const PERMIT_STEP = {
  title: "Your food handler permit",
  line: "A photo of your permit card, or the certificate from your course (PDF or photo).",
  photoHint: "For a photo: flat, with all four edges showing.",
  take: "Take a photo",
  upload: "Upload the PDF or a picture",
  type: "Type it instead",
  notNow: "Not now",
  reading: "Reading it…",
  readingLine: "Saved. You can check the details now or later.",
  checkNow: "Check the details now",
  details: "Check the details",
  unread: "Saved. We couldn't read it, so type what you see, or do it later.",
  readOk: "We filled in what we could read. Check it, and fix anything that's wrong.",
  save: "Save my permit",
  saveCourse: "Save my certificate",
  later: "Check it later",
  saved: "Permit saved",
  savedCourse: "Certificate saved",
  filed: "Saved. Your manager will check it.",
} as const;

/** How long the step waits on the read before it offers the details anyway (typical reads are well under this). Never blocks. */
export const PERMIT_READ_WAIT_MS = 20_000;
export const PERMIT_READ_POLL_MS = 1_000;

/**
 * Where a file picked on the permit step goes. Every photo (camera or library: JPEG, PNG, WebP, HEIC) and every PDF goes to the
 * certificate pipeline (POST /certificate-scans, slotKind food_handler_permit, the person's own id): saved as proof at once,
 * filed for this person, and read for the name, number, issuer, dates and place. Before fix/permit-step-read a JPEG/PNG/WebP
 * skipped the read ("photo"), so a Learn2Serve result uploaded as a picture was never read (owner 2026-10-09, staging: 0 scans).
 * "too_big": over CARD_UPLOAD_MAX_BYTES. The server sniffs the bytes and refuses anything else in plain words.
 */
export function cardFileRoute(f: { mimeType?: string | null; name?: string | null; size?: number | null }): "scan" | "too_big" {
  if (f.size != null && f.size > CARD_UPLOAD_MAX_BYTES) return "too_big";
  return "scan";
}

const addDaysIso = (d: IsoDate, n: number) => new Date(Date.parse(d + "T00:00:00Z") + n * 86400000).toISOString().slice(0, 10);

/**
 * The day a course certificate stops counting, where the place's rule gives it a window (courseCertWindowAt; Utah Code
 * 26B-7-413(3)(c): 30 days after it is issued): the issue day + the window, or the printed end when that is earlier. null
 * when there is no window here or no issue day was read or typed (never guessed); then only a printed end is used.
 */
export function courseGoodUntil(r: { issuedOn?: IsoDate | null; expiresOn?: IsoDate | null }, window: { validDays: number } | null): IsoDate | null {
  if (!window || !r.issuedOn) return r.expiresOn ?? null;
  const end = addDaysIso(r.issuedOn, window.validDays);
  return r.expiresOn && r.expiresOn < end ? r.expiresOn : end;
}

/** "Good until Oct 27 (Utah Code 26B-7-413(3)(c)). Add your food handler permit when it arrives." */
export function courseProofLine(goodUntil: IsoDate | null, citation: string | null, today?: IsoDate): string {
  const tail = "Add your food handler permit when it arrives.";
  if (goodUntil && today && goodUntil < today) return `Your course certificate counted until ${certDay(goodUntil, today)}${citation ? ` (${citation})` : ""}. Add your food handler permit.`;
  return goodUntil ? `Good until ${certDay(goodUntil, today)}${citation ? ` (${citation})` : ""}. ${tail}` : `Your course certificate is saved. ${tail}`;
}

/** What "Check the details" starts with: what was read, or blank. Nothing is ever guessed or carried from another field. */
export interface PermitPrefill { kind: "food_handler_permit" | "food_handler_course"; holderName: string; number: string; issuer: string; issuedOn: string; expiresOn: string; state: string; county: string }
export function permitPrefill(row: Pick<CertScanRowView, "reading" | "filed"> | undefined, read: boolean): PermitPrefill {
  const r = read && row ? row.reading : null;
  // The kind is the server's (services/api certscan/pipeline.ts files a course certificate as one only where the place's rule sets it
  // apart from the permit), so the phone and web never decide it on their own.
  const kind = row?.filed?.kind === "food_handler_course" ? "food_handler_course" : "food_handler_permit";
  return { kind, holderName: r?.holderName ?? "", number: r?.number ?? "", issuer: r?.issuer ?? "", issuedOn: r?.issuedOn ?? "", expiresOn: r?.expiresOn ?? "", state: r?.state ?? "", county: r?.county ?? "" };
}

/** The read must be this sure a file is a course certificate before the permit step files it as one (and not as the permit). */
export const COURSE_KIND_MIN = 0.7;

// ── One document, one credential; the temporary certificate vs the permit (fix/permit-temp-display, owner 2026-10-10) ──────
//
// Sources (read 2026-10-10):
//  - Utah Code 26B-7-413(1)(c): "'Food handler permit' means a permit issued by a local health department to allow a person to
//    work as a food handler." (3)(c): the training program awards "a certificate of completion that is valid with any local
//    health department in the state for 30 days after the day on which the certificate is issued". (4)(a)(i): a person obtains
//    the permit by "providing a valid certificate of completion ... to a local health department". (6)(b): "A food handler
//    permit granted after June 30, 2013, is valid for three years from the date of issuance."
//  - Utah Admin. Code R392-103-4(4): "A training provider shall promptly issue a certificate to any food handler applicant who
//    ... passes"; (8): "Upon issuance, the certificate shall be valid for 30 days."; (9): "A local health officer shall issue a
//    food handler permit when ..."; (10)(d)-(e): the permit's number "includes the training provider's 2-letter unique
//    identifier" and it names the "local health department issuing the permit".
//  - 360training (Learn2Serve) Utah page: "Temporary certificate available immediately, valid for 30 days." "Health department
//    mails official permit within 30 days."
// So a course provider's paper (Learn2Serve, StateFoodSafety, ServSafe ...) is the temporary certificate, never the permit; the
// permit comes from the health department, and its number may repeat the certificate's, so the issuer tells them apart.

/** How many days before a temporary certificate's last day the person and their manager are reminded. */
export const TEMP_CERT_REMIND_DAYS = 7;

/** What the person reads for a course certificate where the place's rule gives it a window (Utah Code 26B-7-413(3)(c)). */
export const TEMP_CERT_TITLE = "Temporary food handler certificate";
export const TEMP_CERT_NEXT = "Next: add your food handler permit when it arrives";

/**
 * Course providers (R392-103-4(4): "A training provider shall promptly issue a certificate"): their paper is the temporary
 * certificate, whatever else the issuer line says. Checked before the health department words below.
 */
const COURSE_PROVIDER = /\bprovider\b|360\s*training|learn\s*2\s*serve|serv\s*safe|state\s*food\s*safety/i;
/** Words that name a health authority (R392-103-4(10)(e): the permit names "the local health department issuing" it). */
const HEALTH_AUTHORITY = /\bhealth\b|\bcounty\b|\bdepartment\b|\bdistrict\b|\bboard of health\b/i;
const squash = (x: string) => x.normalize("NFKD").toLowerCase().replace(/[^a-z0-9]/g, "");

/**
 * An issuer that is a health department: never a course provider; a health authority's words; or one of Utah's 13 local health
 * departments by name or short name (states/ut-local.ts LOCAL_DEPARTMENTS, read at call time: the modules import each other).
 */
export function isHealthDepartment(issuer: string | null | undefined): boolean {
  if (!issuer || COURSE_PROVIDER.test(issuer)) return false;
  if (HEALTH_AUTHORITY.test(issuer)) return true;
  const i = squash(issuer);
  return LOCAL_DEPARTMENTS.some((d) => d.state === "UT" && (i.includes(squash(d.name)) || i === squash(d.short)));
}

const FOOD_HANDLER = (k: string) => k === "food_handler_course" || k === "food_handler_permit";
const dayDiff = (a: IsoDate, b: IsoDate) => Math.round((Date.parse(b + "T00:00:00Z") - Date.parse(a + "T00:00:00Z")) / 86400000);

/**
 * Course certificate or permit, by the place's rule (null window = the state sets no certificate window: the model's kind
 * stands). Only food handler reads change. A health department issuer is the permit; a course provider issuer (read clearly)
 * is the certificate; failing both, a 30-day span between the printed dates is the certificate (the permit runs 3 years).
 * `byRule` names why, when the rule decided.
 */
export function foodHandlerKindByRule(r: Pick<CertReading, "kind" | "issuer" | "issuedOn" | "expiresOn" | "confidence">, window: { validDays: number } | null): { kind: CertReadKind; byRule: "issuer" | "dates" | null } {
  if (!window || !FOOD_HANDLER(r.kind)) return { kind: r.kind, byRule: null };
  if (r.issuer && r.confidence.issuer >= COURSE_KIND_MIN) return { kind: isHealthDepartment(r.issuer) ? "food_handler_permit" : "food_handler_course", byRule: "issuer" };
  if (r.issuedOn && r.expiresOn && dayDiff(r.issuedOn, r.expiresOn) <= window.validDays + 1) return { kind: "food_handler_course", byRule: "dates" };
  return { kind: r.kind, byRule: null };
}

/** Apply the rule to each reading. A kind the rule decided carries the confidence of what decided it (the issuer or dates). */
export function classifyReadings(readings: readonly CertReading[], window: { validDays: number } | null): CertReading[] {
  return readings.map((r) => {
    const c = foodHandlerKindByRule(r, window);
    if (!c.byRule) return r;
    // The rule decided: the kind is as sure as what decided it (the issuer, or both dates), whether it agreed with the model or not.
    const sure = c.byRule === "issuer" ? r.confidence.issuer : Math.min(r.confidence.issuedOn, r.confidence.expiresOn);
    return { ...r, kind: c.kind, confidence: { ...r.confidence, kind: c.kind === r.kind ? Math.max(r.confidence.kind, sure) : sure } };
  });
}

const norm = (s: string | null | undefined) => (s ?? "").normalize("NFKD").toLowerCase().replace(/[^a-z0-9]/g, "");

/**
 * The same paper, read or filed twice: food handler kinds, the same number, and the same issuer (both printed), and the same
 * name when both have one. The issuer is required: the permit's number may repeat the certificate's (R392-103-4(10)(d)), so a
 * permit from the health department is never folded into the course certificate it came from.
 */
export function sameDocument(a: { kind: string; number?: string | null; issuer?: string | null; holderName?: string | null }, b: typeof a): boolean {
  if (!FOOD_HANDLER(a.kind) || !FOOD_HANDLER(b.kind)) return false;
  if (!norm(a.number) || norm(a.number) !== norm(b.number)) return false;
  if (!norm(a.issuer) || norm(a.issuer) !== norm(b.issuer)) return false;
  if (a.holderName && b.holderName && nameTokens(a.holderName).join(" ") !== nameTokens(b.holderName).join(" ")) return false;
  return true;
}

/**
 * One file, one row per document: readings of the same paper (a model may list the Learn2Serve certificate once as the course
 * and once as the "permit") fold into one, after the rule picks the kind. Fields keep the surer read.
 */
export function dedupeReadings(readings: readonly CertReading[], window: { validDays: number } | null): CertReading[] {
  const out: CertReading[] = [];
  for (const r of classifyReadings(readings, window)) {
    const i = out.findIndex((x) => sameDocument(x, r));
    if (i < 0) { out.push(r); continue; }
    const a = out[i]!;
    const pick = <K extends keyof CertReading>(f: K, cf: CertField) => (a[f] === null || (r[f] !== null && r.confidence[cf] > a.confidence[cf]) ? r[f] : a[f]);
    const merged: CertReading = {
      ...a,
      kind: a.confidence.kind >= r.confidence.kind ? a.kind : r.kind,
      holderName: pick("holderName", "holderName"), issuer: pick("issuer", "issuer"), number: pick("number", "number"),
      issuedOn: pick("issuedOn", "issuedOn"), expiresOn: pick("expiresOn", "expiresOn"), state: pick("state", "state"), county: pick("county", "county"),
      verifyNote: a.verifyNote ?? r.verifyNote, verifyUrl: a.verifyUrl ?? r.verifyUrl,
      confidence: Object.fromEntries(CERT_FIELDS.map((f) => [f, Math.max(a.confidence[f], r.confidence[f])])) as Record<CertField, number>,
    };
    out[i] = classifyReadings([merged], window)[0]!;
  }
  return out;
}

/** A group of one person's credentials that are the same paper: the one kept (merged) and the ones folded into it. */
export interface CredentialMerge { keep: Credential; drop: Credential[] }

/**
 * Duplicates already on file (filed before one scan made one row): per person, the same paper filed twice. The kept record is
 * the rule's kind (issuer, then dates), then the checked one, then the one with the photo, then the first filed; it takes any
 * field it lacks, the photo, the scan and a manager's check of the same kind from the others. A course certificate's end is
 * cut to its window (Utah Code 26B-7-413(3)(c)).
 */
export function duplicateCredentials(creds: readonly Credential[], window: { validDays: number } | null): CredentialMerge[] {
  const groups: Credential[][] = [];
  for (const c of creds) {
    const g = groups.find((x) => x.some((y) => y.employeeId === c.employeeId && sameDocument(y, c)));
    if (g) g.push(c); else groups.push([c]);
  }
  return groups.filter((g) => g.length > 1).map((g) => {
    // Filed fields were confirmed by a person (or kept from a sure read), so the rule reads them as certain.
    const sure = Object.fromEntries(CERT_FIELDS.map((f) => [f, 1])) as Record<CertField, number>;
    const rule = foodHandlerKindByRule({ kind: g[0]!.kind as CertReadKind, issuer: g.find((c) => c.issuer)?.issuer ?? null, issuedOn: g.find((c) => c.issuedOn)?.issuedOn ?? null, expiresOn: g.find((c) => c.expiresOn)?.expiresOn ?? null, confidence: sure }, window);
    const want = rule.byRule ? rule.kind : null;
    const rank = (c: Credential) => [want && c.kind === want ? 0 : 1, c.verifiedAt ? 0 : 1, c.photoPath ? 0 : 1];
    const sorted = [...g].sort((a, b) => { const x = rank(a), y = rank(b); return x[0]! - y[0]! || x[1]! - y[1]! || x[2]! - y[2]! || a.id.localeCompare(b.id); });
    const first = sorted[0]!;
    const kind = (want ?? first.kind) as CredentialKind;
    let keep: Credential = { ...first, kind };
    for (const o of sorted.slice(1)) {
      for (const f of ["holderName", "number", "issuedOn", "expiresOn", "issuingCounty", "issuer", "scanId", "photoPath"] as const) if (keep[f] === undefined && o[f] !== undefined) keep = { ...keep, [f]: o[f] };
      if (!keep.verifiedAt && o.verifiedAt && o.kind === kind) keep = { ...keep, verifiedAt: o.verifiedAt, ...(o.verifiedBy ? { verifiedBy: o.verifiedBy } : {}) };
    }
    if (kind === "food_handler_course") { const end = courseGoodUntil(keep, window); if (end) keep = { ...keep, expiresOn: end }; }
    return { keep, drop: sorted.slice(1) };
  });
}

/** One credential row on a person's page: what it is and its facts, in the person's words. */
export function credentialRowText(c: Pick<Credential, "kind" | "holderName" | "number" | "issuer" | "issuingCounty" | "expiresOn" | "scanId" | "photoPath">, o: { today: IsoDate; window: { validDays: number } | null; course?: CourseOnFile | null }): { title: string; detail: string } {
  const temp = c.kind === "food_handler_course" && !!o.window;
  const title = temp ? TEMP_CERT_TITLE : c.kind === "choking_first_aid" ? "Choking first aid" : CERT_KIND_LABEL[c.kind as CertReadKind] ?? c.kind;
  const until = (() => {
    if (!temp || !c.expiresOn) return c.expiresOn ? `expires ${c.expiresOn}` : null;
    if (c.expiresOn < o.today) return `ended ${certDay(c.expiresOn, o.today)}`;
    const by = o.course?.permitBy;
    // The person may work on it until its last day, or the last day of their own 30 days (Utah Code 26B-7-413(2)(b)) when sooner.
    const work = by && by < o.today ? null : by && by < c.expiresOn ? `you can work until ${certDay(by, o.today)}` : "you can work";
    return [`good until ${certDay(c.expiresOn, o.today)}`, work].filter(Boolean).join(" · ");
  })();
  const detail = [c.holderName && `name on it: ${c.holderName}`, c.number && `#${c.number}`, c.issuer, c.issuingCounty && `${c.issuingCounty} County`, until, c.scanId ? "saved as proof" : c.photoPath && "photo on file"].filter(Boolean).join(" · ");
  return { title, detail };
}

/**
 * The summary on a person's page, from ONE function so its lines can't disagree: the headline (the permit on file, the
 * temporary certificate, or nothing yet), what is still needed (missing), and what is on file but runs out soon (renew).
 * A permit on file never sits beside "Still needed: Food handler permit": with a card on file, outstandingSlots only ever
 * offers the permit as a renewal.
 */
export interface CredentialSummary {
  headline: string;
  needed: CertSlot[];
  renew: CertSlot[];
}
export function credentialSummary(st: { card: { kind: string; expiresOn: string | null; verified: boolean; daysLeft: number | null } | null; course: CourseOnFile | null }, slots: readonly CertSlot[], o: { today: IsoDate; window: { validDays: number } | null; noneLine: string; cardLine: string }): CredentialSummary {
  let headline = st.card ? o.cardLine : o.noneLine;
  if (!st.card && st.course) {
    if (o.window && st.course.expiresOn) {
      const ended = st.course.daysLeft !== null && st.course.daysLeft < 0;
      headline = ended ? `${TEMP_CERT_TITLE} ended ${certDay(st.course.permitBy ?? st.course.expiresOn, o.today)}. The permit isn't on file yet.` : `${TEMP_CERT_TITLE}, good until ${certDay(st.course.expiresOn, o.today)}.${st.course.permitBy && st.course.permitBy < st.course.expiresOn ? ` You can work until ${certDay(st.course.permitBy, o.today)}.` : " You can work."}`;
    } else headline = courseLine(st.course, o.today);
  }
  const needed = slots.filter((s) => !s.renew).map((s) => (s.kind === "food_handler_permit" && st.course && !st.card
    ? { ...s, label: `${TEMP_CERT_NEXT}${st.course.permitBy ? ` (by ${certDay(st.course.permitBy, o.today)})` : ""}` } : s));
  // A card on file: the permit is never "still needed" (the guard holds even if a caller passes stale slots).
  return { headline, needed: st.card ? needed.filter((s) => s.kind !== st.card!.kind) : needed, renew: slots.filter((s) => s.renew) };
}
